import * as React from "react";
import axios from "axios";
import {
  Box,
  Grid,
  TextField,
  Button,
  MenuItem,
  Typography,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SimpleTable from "../../components/SimpleTable";
import { tokens } from "../../theme";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

/* ---------- API ---------- */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  headers: { "Content-Type": "application/json" },
});

export default function MySales() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [sales, setSales] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [detailSale, setDetailSale] = React.useState(null);

  const [filters, setFilters] = React.useState({
    customerId: "",
    itemId: "",
    warehouseId: "",
    tinNo: "",
    date: "",
    startDate: "",
    endDate: "",
  });

  const [summary, setSummary] = React.useState({
    count: 0,
    totalSales: 0,
    totalPaid: 0,
    outstanding: 0,
  });

  const [items, setItems] = React.useState([]);
  const [customers, setCustomers] = React.useState([]);
  const [warehouses, setWarehouses] = React.useState([]);

  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.id || user?.user_id;

  const roleRaw = String(
    user?.role || user?.userRole || user?.roleName || user?.type || ""
  ).toLowerCase();

  const isSalesMan =
    /(sales\s*-?\s*man|salesman|sales\s*person|salesperson|^sales$)/i.test(
      roleRaw
    );

  /* ---------- FETCH SALES ---------- */
  const fetchInitial = React.useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const { data } = await api.post(`/api/sales/user/${userId}/filter`, {});
      setSales(data?.sales || []);
      setSummary({
        count: data.count || 0,
        totalSales: data.totalSales || 0,
        totalPaid: data.totalPaid || 0,
        outstanding: data.outstanding || 0,
      });
    } catch (err) {
      console.error(err);
      setSales([]);
    }
    setLoading(false);
  }, [userId]);

  /* ---------- LOAD FILTER LISTS ---------- */
  const loadFilterData = async () => {
    try {
      const [itemRes, custRes, whRes] = await Promise.all([
        api.get("/api/item"),
        api.get("/api/customer"),
        api.get("/api/warehouse"),
      ]);
      setItems(itemRes.data || []);
      setCustomers(custRes.data || []);
      setWarehouses(whRes.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  React.useEffect(() => {
    if (!userId) return;
    fetchInitial();
    loadFilterData();
  }, [fetchInitial, userId]);

  /* ---------- FILTER HANDLERS ---------- */
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = async () => {
    const payload = {};
    Object.keys(filters).forEach((key) => {
      if (filters[key]) payload[key] = filters[key];
    });

    try {
      const { data } = await api.post(`/api/sales/user/${userId}/filter`, payload);
      setSales(data.sales || []);
      setSummary({
        count: data.count || 0,
        totalSales: data.totalSales || 0,
        totalPaid: data.totalPaid || 0,
        outstanding: data.outstanding || 0,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const clearFilters = () => {
    setFilters({
      customerId: "",
      itemId: "",
      warehouseId: "",
      tinNo: "",
      date: "",
      startDate: "",
      endDate: "",
    });
    fetchInitial();
  };

  /* ---------- SIMPLETABLE COLUMNS ---------- */
  const columns = [
    {
      field: "no",
      headerName: "No.",
      width: 80,
      sortable: false,
      valueGetter: (p) => p.api.getRowIndexRelativeToVisibleRows(p.id) + 1,
    },
    { field: "Customer.name", headerName: "Customer", width: 180, valueGetter: (p) => p.row.Customer?.name || "N/A" },
    { field: "Warehouse.name", headerName: "Warehouse", width: 150, valueGetter: (p) => p.row.Warehouse?.name },
    { field: "totalPrice", headerName: "Total", width: 120 },
    { field: "paidAmount", headerName: "Paid", width: 120 },
    { field: "salesDate", headerName: "Date", width: 130, valueGetter: (p) => dayjs.utc(p.row.salesDate).format("YYYY-MM-DD") },
    { field: "tinNo", headerName: "TIN No", width: 120 },
    { field: "fsNo", headerName: "FS No", width: 120 },
    { field: "machineNo", headerName: "Machine No", width: 100 },
     {
      field: "detail",
      headerName: "",
      width: 50,
      sortable: false,
      renderCell: (params) => (
        <IconButton onClick={() => setDetailSale(params.row)}>
          <InfoOutlinedIcon />
        </IconButton>
      ),
    },
  ];

  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: colors.primary[400],
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
  };

  return (
    <Box m="20px">
      <Typography variant="h5" gutterBottom>
        My Sales Records {isSalesMan && "(Sales)"}
      </Typography>

      {/* FILTERS */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={2}>
          <TextField select fullWidth label="Customer" value={filters.customerId} onChange={(e) => handleFilterChange("customerId", e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {customers.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField select fullWidth label="Item" value={filters.itemId} onChange={(e) => handleFilterChange("itemId", e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {items.map((i) => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField select fullWidth label="Warehouse" value={filters.warehouseId} onChange={(e) => handleFilterChange("warehouseId", e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {warehouses.map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField label="TIN No" fullWidth value={filters.tinNo} onChange={(e) => handleFilterChange("tinNo", e.target.value)} />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField type="date" label="Date" fullWidth InputLabelProps={{ shrink: true }} value={filters.date} onChange={(e) => handleFilterChange("date", e.target.value)} />
        </Grid>
        <Grid item xs={12} md={2}>
          <Button fullWidth variant="contained" onClick={applyFilters}>Apply</Button>
        </Grid>
        <Grid item xs={12} md={2}>
          <Button fullWidth variant="outlined" onClick={clearFilters}>Clear</Button>
        </Grid>
      </Grid>

      {/* SUMMARY */}
      <Box sx={{ mb: 2, p: 2, border: "1px solid #ddd", borderRadius: 2 }}>
        <Typography>Total Sales: {summary.count}</Typography>
        <Typography>Total Amount: {summary.totalSales.toFixed(2)}</Typography>
        <Typography>Paid: {summary.totalPaid.toFixed(2)}</Typography>
        <Typography>Outstanding: {summary.outstanding.toFixed(2)}</Typography>
      </Box>

      {/* SIMPLE TABLE */}
      <SimpleTable
        rows={sales}
        columns={columns}
        loading={loading}
        getRowId={(r) => r.id}
        height="70vh"
        sx={gridSx}
      />

      {/* DETAIL MODAL */}
      <Dialog open={!!detailSale} onClose={() => setDetailSale(null)} maxWidth="md" fullWidth>
        <DialogTitle>Sale Items Detail</DialogTitle>
        <DialogContent>
          {detailSale && (
            <Box>
             
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Unit Price</TableCell>
                    <TableCell>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailSale.SalesItems.map((si) => (
                    <TableRow key={si.id}>
                      <TableCell>{si.Item?.name || "N/A"}</TableCell>
                      <TableCell>{si.quantity}</TableCell>
                      <TableCell>{Number(si.unitPrice).toFixed(2)}</TableCell>
                      <TableCell>{Number(si.totalPrice).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="body2">
                Total Price: {Number(detailSale.totalPrice).toFixed(2)} | Paid: {Number(detailSale.paidAmount).toFixed(2)} | VAT: {Number(detailSale.vat).toFixed(2)}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
